import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaEnvelope, FaCopy } from 'react-icons/fa';
import PopupComponent from './PopupComponent';
import { generateShareableLink, shareCatalogueLink } from '../services/productService';
import { toast } from 'react-hot-toast';
import './ShareCatalogueComponent.css';
import logger from '../utils/logger';

interface ShareCatalogueComponentProps {
  isOpen: boolean;
  onClose: () => void;
  productIds: string[];
  leadId?: string;
  leadName?: string;
}

const ShareCatalogueComponent: React.FC<ShareCatalogueComponentProps> = ({
  isOpen,
  onClose,
  productIds,
  leadId,
  leadName
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [shareLink, setShareLink] = useState('');

  useEffect(() => {
    if (isOpen && productIds.length > 0) {
      generateLink();
    }
  }, [isOpen, productIds, leadId, leadName]);

  const generateLink = async () => {
    setIsLoading(true);
    logger.log('Generating shareable link for products:', productIds);
    
    try {
      // Pass lead information if available
      const response = await generateShareableLink(
        productIds, 
        leadId ? { leadId, leadName } : undefined
      );
      logger.log('Share link generated successfully:', response);
      
      // Fix: Correctly access the link from the nested data structure
      // The API response has the link in response.data.data.link
      if (response && response.data && response.data.data && response.data.data.link) {
        const link = response.data.data.link;
        logger.log('Extracted share link:', link);
        setShareLink(link);
      } else {
        console.error('Unexpected API response structure:', response);
        toast.error('Unable to generate link: Invalid response format');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error generating shareable link:', error);
      toast.error('Failed to generate shareable link');
      setIsLoading(false);
    }
  };

  const handleShare = (method: 'whatsapp' | 'email' | 'copy') => {
    logger.log(`Sharing catalogue via ${method}`);
    shareCatalogueLink(shareLink, method);
    
    // Close popup after short delay for better UX
    if (method === 'copy') {
      setTimeout(onClose, 1500);
    } else {
      onClose();
    }
  };



return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Share Product Catalogue"
      size="md"
      loading={isLoading}
    >
      <div className="share-catalogue-container">
        {!isLoading && (
          <>
            <div className="share-catalogue-header">
              <h3>Your catalogue is ready to share</h3>
              <div className="link-container">
                <input 
                  type="text" 
                  value={shareLink} 
                  readOnly 
                  className="share-link-input"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button 
                  className="copy-link-button"
                  onClick={() => handleShare('copy')}
                >
                  <FaCopy size={16} />
                  Copy
                </button>
              </div>
            </div>
            
            <div className="share-options">
              <h4>Share via</h4>
              <div className="share-buttons">
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="share-button whatsapp"
                >
                  <FaWhatsapp size={20} />
                  <span>WhatsApp</span>
                </button>
                
                <button
                  onClick={() => handleShare('email')}
                  className="share-button email"
                >
                  <FaEnvelope size={20} />
                  <span>Email</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </PopupComponent>
  );
};

export default ShareCatalogueComponent;